// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { OrdersDataViewContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Modal, { ModalPresentationProps } from "@store/MobiusOverrides/Modal/Modal";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { getDocumentsWrapper } from "@store/Framework/Common/Utilities/getDocumentsWrapper";
import useTrackExportHistory from "@store/Framework/GoogleAnalytics/hooks/useTrackExportHistory";
import { getOrderHistory } from "@store/Framework/Services/documentService";
import { useGreifOrderHistoryExportSettings } from "@store/Framework/Store/Context/ContextHooks";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import { tableFilterProps } from "@store/Themes/common/props/table";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { useOrderHistoryState } from "@store/Framework/Store/Greif/Pages/OrderHistory/OrderHistoryHooks";
import React, { FC, useContext, useMemo, useState } from "react";

const dateDiffInDays = (a: Date, b: Date) => {
    const _MS_PER_DAY = 1000 * 60 * 60 * 24;
    // Discard the time and time-zone information.
    const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

    return Math.floor((utc2 - utc1) / _MS_PER_DAY);
};

const checkEnforcementAndValueValidity = (value: number, allowedMaxValue: number, greater?: boolean) => {
    if (value > 0) {
        return greater ? value >= allowedMaxValue : value <= allowedMaxValue;
    }
    return false;
};

export interface OrderHistorySearchExportButtonProps extends WidgetProps {}

export interface OrderHistorySearchExportButtonStyles {
    conditionWarning?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    loadingLink?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    tooltip?: TooltipPresentationProps;
    wrapper?: InjectableCss;
}

export const orderHistorySearchExportButtonStyles: OrderHistorySearchExportButtonStyles = {
    link: tableFilterProps.exportLink,
    loadingLink: tableFilterProps.exportLoadingLink,
    modal: tableFilterProps.exportModal,
    tooltip: tableFilterProps.exportTooltip,
    conditionWarning: tableFilterProps.exportModalText,
};

const styles = orderHistorySearchExportButtonStyles;

const OrderHistorySearchExportButton: FC<OrderHistorySearchExportButtonProps> = () => {
    const { fromDate, toDate, fromDeliveryDate, toDeliveryDate } = useOrderHistoryState().parameter;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const fromOrderDateCompare = useMemo(() => new Date(fromDate!), [fromDate]);
    const toOrderDateCompare = useMemo(() => new Date(toDate || new Date()), [toDate]);
    const fromDeliveryDateCompare = useMemo(() => new Date(fromDeliveryDate!), [fromDeliveryDate]);
    const toDeliveryDateCompare = useMemo(() => new Date(toDeliveryDate || new Date()), [toDeliveryDate]);

    const toasterContext = useContext(ToasterContext);
    const ordersDataView = usePersistentDataView(useContext(OrdersDataViewContext));
    const trackExportHistory = useTrackExportHistory();

    const {
        enforceMaxNumberOfImports,
        enforceMaxDateRangeOfImports,
        maxNumberOfImports = 50,
        maxDateRangeOfImports = 90,
    } = useGreifOrderHistoryExportSettings() ?? {};

    let totalItemCount = 0;

    if (ordersDataView.value && ordersDataView.pagination) {
        totalItemCount = ordersDataView?.pagination.totalItemCount;
    }

    const exportOrderHistory = () => {
        const isValidDateRanges = () => {
            const numberOfOrderDaysFiltered = dateDiffInDays(fromOrderDateCompare!, toOrderDateCompare!);
            const numberOfDeliveryDaysFiltered = dateDiffInDays(fromDeliveryDateCompare!, toDeliveryDateCompare!);

            if (numberOfOrderDaysFiltered && !numberOfDeliveryDaysFiltered) {
                return checkEnforcementAndValueValidity(numberOfOrderDaysFiltered, maxDateRangeOfImports);
            }

            if (numberOfDeliveryDaysFiltered && !numberOfOrderDaysFiltered) {
                return checkEnforcementAndValueValidity(numberOfDeliveryDaysFiltered, maxDateRangeOfImports);
            }

            if (numberOfOrderDaysFiltered && numberOfDeliveryDaysFiltered) {
                const greaterDateFrom =
                    fromOrderDateCompare! < fromDeliveryDateCompare! ? fromOrderDateCompare : fromDeliveryDateCompare;

                const greaterDateTo =
                    toOrderDateCompare! < toDeliveryDateCompare! ? toOrderDateCompare : toDeliveryDateCompare;

                const numberOfCombinedDaysFilters = dateDiffInDays(greaterDateFrom!, greaterDateTo!);

                return checkEnforcementAndValueValidity(numberOfCombinedDaysFilters, maxDateRangeOfImports);
            }

            return false;
        };

        const canExport = () => {
            const isValidNumberOfOrders = checkEnforcementAndValueValidity(totalItemCount, maxNumberOfImports);

            if (enforceMaxNumberOfImports && enforceMaxDateRangeOfImports) {
                return isValidNumberOfOrders && isValidDateRanges();
            }

            if (enforceMaxNumberOfImports && !enforceMaxDateRangeOfImports) {
                return isValidNumberOfOrders;
            }

            if (!enforceMaxNumberOfImports && enforceMaxDateRangeOfImports) {
                return isValidDateRanges();
            }

            return false;
        };

        if ((!enforceMaxNumberOfImports && !enforceMaxDateRangeOfImports) || canExport()) {
            getDocumentsWrapper(
                getOrderHistory,
                { parameter: window.location.search },
                { stateSetter: setIsExporting, toasterContext },
            ).then(e => {
                if (e) {
                    trackExportHistory({ file_category: "OrderHistory export" });
                }
            });
        } else {
            setIsModalOpen(true);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Link
                {...(isExporting ? styles.loadingLink : styles.link)}
                data-test-selector="orderHistory_exportFilters"
                disabled={isExporting || ordersDataView.isLoading || totalItemCount === 0}
                onClick={exportOrderHistory}
            >
                {translate("Export Order History")}
            </Link>
            <Tooltip
                {...styles.tooltip}
                text={translate("Exports orders based on your current filters. Remove filters to export all orders.")}
            />

            <Modal
                {...styles.modal}
                headline={translate("Export Order History")}
                isOpen={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
            >
                <Typography {...styles.conditionWarning}>
                    {enforceMaxNumberOfImports && enforceMaxDateRangeOfImports
                        ? translate("Please ensure that the conditions below are respected.")
                        : translate("Please ensure that the condition below is respected.")}
                </Typography>
                <UnorderedList>
                    {enforceMaxNumberOfImports && (
                        <ListItem>
                            <Typography>
                                {siteMessage("OrderHistory_CantExport_NumberOrders", String(maxNumberOfImports))}
                            </Typography>
                        </ListItem>
                    )}
                    {enforceMaxDateRangeOfImports && (
                        <ListItem>
                            <Typography>
                                {siteMessage("OrderHistory_CantExport_DateRangeDays", String(maxDateRangeOfImports))}
                            </Typography>
                        </ListItem>
                    )}
                </UnorderedList>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderHistorySearchExportButton,
    definition: {
        group: "Order History",
        displayName: "Export History Button",
        allowedContexts: [],
    },
};

export default widgetModule;
